import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

// ✨ Changed: Updated to the consistent container style from your other components.
const StyledTableContainer = styled(TableContainer)({
    backgroundColor: '#202020',
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
});

// This component is already well-styled and consistent.
const SectionHeader = styled(Typography)({
    marginBottom: '1rem',
    fontWeight: 'bold',
    color: '#12c099',
});

// ✨ Changed: Renamed for clarity. This style is for header cells.
const HeaderTableCell = styled(TableCell)({
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 'bold',
    padding: '16px 20px', // Added consistent padding
    borderBottom: '1px solid #404040', // A slightly stronger border for the header
});

// ✨ Added: A new component for body cells to match the main design (less prominent text).
const BodyTableCell = styled(TableCell)({
    color: '#aaa',
    fontSize: '1rem',
    padding: '16px 20px',
    borderBottom: '1px solid #404040',
});

const ProfitLoss = ({ profitLossData }) => {
    console.log("🔍 Debug: profitLossData received", profitLossData);

    const isEmpty = !profitLossData || typeof profitLossData !== 'object' || Object.keys(profitLossData).length === 0;

    // A helper array to structure the data cleanly.
    const dataEntries = !isEmpty ? [
        { category: "Coach Payments", amount: Array.isArray(profitLossData.coachPayments) ? profitLossData.coachPayments.reduce((sum, p) => sum + (p.paid || 0), 0) : 0 },
        { category: "Site Manager Payments", amount: Array.isArray(profitLossData.siteManagerPayments) ? profitLossData.siteManagerPayments.reduce((sum, p) => sum + (p.paid || 0), 0) : 0 },
        { category: "Venue Fees", amount: Array.isArray(profitLossData.venueFees) ? profitLossData.venueFees.reduce((sum, p) => sum + (p.paid || 0), 0) : 0 },
        { category: "Insurance Fees", amount: profitLossData.insuranceFees?.paid || 0 },
        { category: "Other Fees", amount: Array.isArray(profitLossData.otherFees) ? profitLossData.otherFees.reduce((sum, fee) => sum + (fee.amount || 0), 0) : 0 },
        { category: "Total Revenue", amount: profitLossData.sessionRevenue?.totalRevenue || 0, isBold: true },
        { category: "Projected Profit", amount: profitLossData.projectedProfit || 0 },
        { category: "Actual Profit", amount: profitLossData.actualProfit || 0, isBold: true },
    ] : [];

    return (
        // ✨ Changed: Replaced fragment with Box for consistent spacing.
        <Box sx={{ mt: 4 }}>
            <SectionHeader variant="h4">Profit/Loss Breakdown</SectionHeader>

            <StyledTableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {/* ✨ Changed: Using the new HeaderTableCell */}
                            <HeaderTableCell>Category</HeaderTableCell>
                            <HeaderTableCell align="right">Amount ($)</HeaderTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!isEmpty ? (
                            dataEntries.map((entry, index) => (
                                <TableRow
                                    key={index}
                                    // ✨ Added: Consistent hover effect.
                                    sx={{
                                        '&:hover': { backgroundColor: '#2b2b2b' },
                                        // Remove border for the very last row
                                        '&:last-child td, &:last-child th': { border: 0 },
                                    }}
                                >
                                    {/* ✨ Changed: Using the new BodyTableCell */}
                                    <BodyTableCell
                                        sx={{
                                            // Make summary rows stand out a bit
                                            color: entry.isBold ? '#fff' : '#aaa',
                                            fontWeight: entry.isBold ? 'bold' : 'normal',
                                        }}
                                    >
                                        {entry.category}
                                    </BodyTableCell>
                                    <BodyTableCell
                                        align="right"
                                        sx={{
                                            color: entry.isBold ? '#fff' : '#aaa',
                                            fontWeight: entry.isBold ? 'bold' : 'normal',
                                        }}
                                    >
                                        {entry.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </BodyTableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <BodyTableCell colSpan={2} align="center" sx={{ color: "#f44336", fontStyle: 'italic' }}>
                                    No financial data available.
                                </BodyTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        </Box>
    );
};

export default ProfitLoss;
